/*
 * <pdp-size-picker>
 *
 * Shared size-picker component for PDPs. Renders bucket cards (Nook /
 * Accent / Focal / Statement) + a sub-size pill grid + an in/cm toggle +
 * an Art Advisor hint. Visually canonical across all PDPs (extracted from
 * the original surgical PDP implementation, which is the design source
 * of truth).
 *
 * Each host PDP feeds the component its size catalogue via JS properties
 * and listens for selection changes via DOM events. The component does
 * not talk to skuPickerInstance, the configurator metaobject, or any
 * other host-specific data source — that wiring lives in the host's JS.
 *
 * Properties (set via JS):
 *   picker.options = [{
 *     handle:         'sz-12x16',
 *     name?:          '12 × 16',          // optional override label
 *     width_inches:   12,
 *     height_inches:  16,
 *     width_cms:      30,
 *     height_cms:     40,
 *     price?:         12000,              // pence/cents (number)
 *     blocked?:       false,              // disables, mutes, prevents selection
 *     warning?:       false,              // soft warning dot
 *     soldOut?:       false,              // strikethrough + disabled
 *     qualityLabel?:  '',                 // e.g. "Great quality" caption
 *   }, ...]
 *   picker.value          = 'sz-12x16'    // currently-selected handle
 *   picker.unit           = 'in' | 'cm'
 *   picker.formatPrice    = fn(cents) => '£120.00'   // host can override Intl default
 *   picker.showAdvisor    = true | false
 *   picker.advisorIntent  = 'size'
 *   picker.headerLabel    = 'Size'        // eyebrow text
 *
 * Events (dispatched on this element, bubbles):
 *   'pdp-size-picker:change'     { handle }
 *   'pdp-size-picker:unit-change' { unit }
 *
 * Bucket classification matches assets/size-impact-slider.js:
 *   Nook ≤12in, Accent ≤16in, Focal ≤32in, Statement >32in.
 */

(() => {
  'use strict';

  const SIZE_BUCKETS = [
    { id: 'XS', name: 'Nook',      max: 12,        desc: 'Petite — clusters and gallery walls.' },
    { id: 'S',  name: 'Accent',    max: 16,        desc: 'Versatile and balanced. Ideal for entryways and desks.' },
    { id: 'M',  name: 'Focal',     max: 32,        desc: 'A confident centerpiece for living rooms and beds.', popular: true },
    { id: 'L',  name: 'Statement', max: Infinity,  desc: 'Floor-to-ceiling drama for big walls.' },
  ];

  function classifyBucket(opt) {
    const w = parseFloat(opt?.width_inches) || 0;
    const h = parseFloat(opt?.height_inches) || 0;
    // Some catalogues only ship cm dimensions — fall back to converted cm.
    let max = Math.max(w, h);
    if (max === 0) {
      const wc = parseFloat(opt?.width_cms) || 0;
      const hc = parseFloat(opt?.height_cms) || 0;
      max = Math.max(wc, hc) / 2.54;
    }
    for (const b of SIZE_BUCKETS) {
      if (max <= b.max) return b;
    }
    return SIZE_BUCKETS[SIZE_BUCKETS.length - 1];
  }

  function defaultFormatPrice(cents) {
    if (cents == null || isNaN(cents)) return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'GBP',
        minimumFractionDigits: 2,
      }).format(cents / 100);
    } catch {
      return `£${(cents / 100).toFixed(2)}`;
    }
  }

  function readUnitPref(el) {
    try {
      const stored = localStorage.getItem('pdp_unit');
      if (stored === 'in' || stored === 'cm') return stored;
    } catch {}
    const attr = el?.getAttribute('default-unit');
    return (attr === 'in' || attr === 'cm') ? attr : 'in';
  }
  function writeUnitPref(u) {
    try {
      localStorage.setItem('pdp_unit', u);
      // Mirror to sku-picker.js's key so the legacy picker (hidden on the
      // surgical PDP, but the source of `properties[Size]` on ATC) ends up
      // formatting size in the same unit the user picked in the surgical UI.
      localStorage.setItem('skuPicker_unitPreference', u);
    } catch {}
  }

  class PdpSizePicker extends HTMLElement {
    constructor() {
      super();
      this._options = [];
      this._value = null;
      this._unit = 'in'; // overridden in connectedCallback with locale-aware default
      this._formatPrice = defaultFormatPrice;
      // Default off — the surgical PDP design hides the inline advisor hint;
      // a host may opt in via `picker.showAdvisor = true` or the `show-advisor`
      // attribute when its own design calls for it.
      this._showAdvisor = false;
      this._advisorIntent = 'size';
      this._headerLabel = 'Size';
      this._connected = false;
      this._flat = false;
      this._curated = [];
      this._recHandle = null;
      this._recLabel = '';
      this._fromLabel = 'from';
      this._seeAllLabel = 'See all sizes';
    }

    // ── Property API ─────────────────────────────────────────────────────
    set options(arr) { this._options = Array.isArray(arr) ? arr.slice() : []; this._renderIfReady(); }
    get options() { return this._options; }

    set value(handle) {
      const next = handle || null;
      if (next === this._value) return;
      this._value = next;
      this._renderIfReady();
    }
    get value() { return this._value; }

    set unit(u) {
      if (u !== 'in' && u !== 'cm') return;
      if (u === this._unit) return;
      this._unit = u;
      writeUnitPref(u);
      this._renderIfReady();
    }
    get unit() { return this._unit; }

    set formatPrice(fn) {
      if (typeof fn !== 'function') return;
      this._formatPrice = fn;
      this._renderIfReady();
    }

    set showAdvisor(b) { this._showAdvisor = !!b; this._renderIfReady(); }
    set advisorIntent(i) { this._advisorIntent = i || 'size'; this._renderIfReady(); }
    set headerLabel(s) { this._headerLabel = s || 'Size'; this._renderIfReady(); }

    set flat(b) { this._flat = !!b; this._renderIfReady(); }
    set curatedHandles(arr) { this._curated = Array.isArray(arr) ? arr.slice() : []; this._renderIfReady(); }
    set recommendedHandle(h) { this._recHandle = h || null; this._renderIfReady(); }
    set recommendedLabel(s) { this._recLabel = s || ''; this._renderIfReady(); }
    set fromLabel(s) { this._fromLabel = s || 'from'; this._renderIfReady(); }
    set seeAllLabel(s) { this._seeAllLabel = s || 'See all sizes'; this._renderIfReady(); }

    // ── Lifecycle ────────────────────────────────────────────────────────
    connectedCallback() {
      this._connected = true;
      this._unit = readUnitPref(this);
      if (this.hasAttribute('show-advisor')) this._showAdvisor = true;
      if (this.hasAttribute('hide-advisor')) this._showAdvisor = false;
      if (this.hasAttribute('advisor-intent')) this._advisorIntent = this.getAttribute('advisor-intent');
      if (this.hasAttribute('header-label')) this._headerLabel = this.getAttribute('header-label');
      this._render();
    }

    disconnectedCallback() {
      this._connected = false;
    }

    // ── Internal helpers ─────────────────────────────────────────────────
    _renderIfReady() { if (this._connected) this._render(); }

    _currentOption() {
      if (!this._options.length) return null;
      const found = this._options.find(o => o.handle === this._value);
      return found || this._options[0];
    }

    _bucketOf(opt) { return opt ? classifyBucket(opt) : null; }

    _byBucket() {
      const groups = {};
      SIZE_BUCKETS.forEach(b => groups[b.id] = []);
      this._options.forEach(o => {
        const b = this._bucketOf(o);
        if (b) groups[b.id].push(o);
      });
      return groups;
    }

    _formatLabel(opt) {
      if (!opt) return '';
      if (opt.name && this._unit === 'in' && (opt.width_inches == null || opt.height_inches == null)) {
        return opt.name;
      }
      if (this._unit === 'cm') {
        const wc = parseFloat(opt.width_cms);
        const hc = parseFloat(opt.height_cms);
        if (wc > 0 && hc > 0) return `${Math.round(wc)} × ${Math.round(hc)} cm`;
      } else {
        const wi = parseFloat(opt.width_inches);
        const hi = parseFloat(opt.height_inches);
        if (wi > 0 && hi > 0) {
          const fmt = (n) => Number.isInteger(n) ? String(n) : n.toFixed(1).replace(/\.0$/, '');
          return `${fmt(wi)} × ${fmt(hi)}″`;
        }
      }
      return opt.name || opt.handle;
    }

    _bucketRangeLabel(list) {
      if (!list || list.length === 0) return '—';
      const unit = this._unit;
      const dims = list.map(s => {
        if (unit === 'cm') return Math.max(parseFloat(s.width_cms) || 0, parseFloat(s.height_cms) || 0);
        return Math.max(parseFloat(s.width_inches) || 0, parseFloat(s.height_inches) || 0);
      }).filter(n => n > 0);
      if (dims.length === 0) return '—';
      const min = Math.min(...dims);
      const max = Math.max(...dims);
      const fmtNum = (n) => Number.isInteger(n) ? String(n) : n.toFixed(1).replace(/\.0$/, '');
      if (unit === 'cm') {
        return min === max ? `${fmtNum(min)} cm` : `${fmtNum(min)}-${fmtNum(max)} cm`;
      }
      return min === max ? `${fmtNum(min)}"` : `${fmtNum(min)}-${fmtNum(max)}"`;
    }

    _render() {
      if (this._flat) return this._renderFlat();
      const fmt = this._formatPrice;
      const groups = this._byBucket();
      const cur = this._currentOption();
      const curBucket = this._bucketOf(cur);
      const unit = this._unit;
      const showAdvisor = this._showAdvisor;
      const advisorIntent = this._advisorIntent;

      const headPair = cur
        ? `${curBucket?.name || ''} (${this._formatLabel(cur)})`
        : '';

      const html = `
        <div class="psp">
          <div class="psp__head">
            <span class="psp__label">
              ${this._headerLabel} · <b>${headPair}</b>
            </span>
            <div class="psp__unit" role="group" aria-label="Unit">
              <button type="button" data-unit="in" aria-pressed="${unit === 'in'}" class="${unit === 'in' ? 'on' : ''}">in</button>
              <button type="button" data-unit="cm" aria-pressed="${unit === 'cm'}" class="${unit === 'cm' ? 'on' : ''}">cm</button>
            </div>
          </div>

          ${showAdvisor ? `
            <button type="button" class="psp__advisor" data-advisor-open data-advisor-intent="${advisorIntent}">
              <span class="dot" aria-hidden="true"></span>
              <span>Not sure? <span class="link">Ask the Art Advisor for the right size</span></span>
            </button>
          ` : ''}

          <div class="psp__buckets">
            ${SIZE_BUCKETS.map(b => {
              const list = groups[b.id] || [];
              const allBlocked = list.length > 0 && list.every(o => o.blocked || o.soldOut);
              const min = list.reduce((m, s) => {
                if (s.blocked || s.soldOut) return m;
                const p = (s.price != null) ? s.price : null;
                return p != null && (m === null || p < m) ? p : m;
              }, null);
              const isOn = curBucket && b.id === curBucket.id;
              const rangeLabel = this._bucketRangeLabel(list);
              const disabled = list.length === 0 || allBlocked;
              return `
                <button type="button"
                        class="psp-bucket ${isOn ? 'on' : ''} ${list.length === 0 ? 'empty' : ''} ${allBlocked ? 'blocked' : ''}"
                        data-bucket="${b.id}"
                        ${disabled ? 'disabled' : ''}>
                  ${b.popular ? '<span class="pop">Popular</span>' : ''}
                  <div class="name">${b.name}</div>
                  <div class="range">${rangeLabel}</div>
                  <div class="from">
                    ${list.length ? `<b>${min !== null ? fmt(min) : '—'}</b>` : '—'}
                  </div>
                </button>`;
            }).join('')}
          </div>

          <div class="psp__subs">
            ${(curBucket ? (groups[curBucket.id] || []) : []).map(s => {
              const isOn = s.handle === cur?.handle;
              const stateClass = [
                isOn ? 'on' : '',
                s.blocked ? 'blocked' : '',
                s.warning ? 'warn' : '',
                s.soldOut ? 'sold-out' : '',
              ].filter(Boolean).join(' ');
              const disabled = s.blocked || s.soldOut;
              const priceStr = (s.price != null) ? fmt(s.price) : '';
              return `
                <button type="button"
                        class="psp-subsize ${stateClass}"
                        data-handle="${s.handle}"
                        ${disabled ? 'disabled aria-disabled="true"' : ''}>
                  <b>${this._formatLabel(s)}</b>
                  ${priceStr ? `<span class="p">${priceStr}</span>` : ''}
                  ${s.qualityLabel ? `<span class="q">${s.qualityLabel}</span>` : ''}
                </button>`;
            }).join('')}
          </div>

          ${curBucket ? `<div class="psp__help">${curBucket.desc}</div>` : ''}
        </div>
      `;
      this.innerHTML = html;
      this._wire();
    }

    _wire() {
      // Bucket clicks — pick smallest available size in that bucket
      // (skip blocked/sold). Same default across every bucket so the
      // user gets the least aggressive starting point and can size up
      // from there.
      this.querySelectorAll('[data-bucket]').forEach(btn => {
        btn.addEventListener('click', () => {
          if (btn.disabled) return;
          const bid = btn.dataset.bucket;
          const groups = this._byBucket();
          const list = (groups[bid] || []).filter(o => !o.blocked && !o.soldOut);
          if (!list.length) return;
          this._emitChange(list[0].handle);
        });
      });
      // Sub-size clicks
      this.querySelectorAll('[data-handle]').forEach(btn => {
        btn.addEventListener('click', () => {
          if (btn.disabled) return;
          this._emitChange(btn.dataset.handle);
        });
      });
      // Unit toggle
      this.querySelectorAll('[data-unit]').forEach(btn => {
        btn.addEventListener('click', () => {
          const next = btn.dataset.unit;
          if (next === this._unit) return;
          this._unit = next;
          writeUnitPref(next);
          this.dispatchEvent(new CustomEvent('pdp-size-picker:unit-change', {
            bubbles: true,
            detail: { unit: next },
          }));
          this._render();
        });
      });
    }

    _renderFlat() {
      const fmt = this._formatPrice;
      const unit = this._unit;
      const cur = this._currentOption();
      const byHandle = (h) => this._options.find(o => o.handle === h);
      let cards = (this._curated || []).map(byHandle).filter(Boolean);
      // If the chosen size isn't one of the curated few, swap it into the card of the
      // SAME size category (Nook/Accent/Focal/Statement) rather than appending a 5th.
      if (cur && !cards.some(o => o.handle === cur.handle)) {
        const curBucketId = this._bucketOf(cur)?.id;
        const idx = cards.findIndex(o => this._bucketOf(o)?.id === curBucketId);
        if (idx >= 0) cards[idx] = cur;
        else cards = cards.concat(cur);
      }

      const head = cur ? (this._bucketOf(cur)?.name || cur.name || this._formatLabel(cur)) : '';
      const html = `
        <div class="psp psp--flat">
          <div class="psp__head">
            <span class="psp__label">${this._headerLabel} · <b>${head}${cur ? ` (${this._formatLabel(cur)})` : ''}</b></span>
            <div class="psp__unit" role="group" aria-label="Unit">
              <button type="button" data-unit="in" aria-pressed="${unit === 'in'}" class="${unit === 'in' ? 'on' : ''}">in</button>
              <button type="button" data-unit="cm" aria-pressed="${unit === 'cm'}" class="${unit === 'cm' ? 'on' : ''}">cm</button>
            </div>
          </div>
          <div class="psp__flatrow">
            ${cards.map(s => {
              const isOn = s.handle === cur?.handle;
              const isRec = s.handle === this._recHandle;
              const disabled = s.blocked || s.soldOut;
              const dim = this._formatLabel(s);
              const nameLine = this._bucketOf(s)?.name || s.name || dim;
              const showDim = nameLine !== dim;
              const priceStr = (s.price != null) ? fmt(s.price) : '';
              return `
                <button type="button" class="psp-flatcard ${isOn ? 'on' : ''} ${disabled ? 'blocked' : ''}"
                        data-handle="${s.handle}" ${disabled ? 'disabled aria-disabled="true"' : ''}>
                  ${isRec && this._recLabel ? `<span class="rec">${this._recLabel}</span>` : ''}
                  <div class="name">${nameLine}</div>
                  ${showDim ? `<div class="dim">${dim}</div>` : ''}
                  ${priceStr ? `<div class="from">${priceStr}</div>` : ''}
                </button>`;
            }).join('')}
          </div>
          <button type="button" class="psp__seeall" data-see-all>
            <span class="left"><span>${this._seeAllLabel}</span></span>
            <span class="arr">→</span>
          </button>
        </div>`;
      this.innerHTML = html;
      this._wireFlat();
    }

    _wireFlat() {
      this.querySelectorAll('.psp-flatcard[data-handle]').forEach(btn => {
        btn.addEventListener('click', () => { if (!btn.disabled) this._emitChange(btn.dataset.handle); });
      });
      this.querySelectorAll('[data-unit]').forEach(btn => {
        btn.addEventListener('click', () => {
          const next = btn.dataset.unit;
          if (next === this._unit) return;
          this._unit = next; writeUnitPref(next);
          this.dispatchEvent(new CustomEvent('pdp-size-picker:unit-change', { bubbles: true, detail: { unit: next } }));
          this._render();
        });
      });
      const sa = this.querySelector('[data-see-all]');
      if (sa) sa.addEventListener('click', () => this.dispatchEvent(new CustomEvent('pdp-size-picker:see-all', { bubbles: true })));
    }

    _emitChange(handle) {
      this.dispatchEvent(new CustomEvent('pdp-size-picker:change', {
        bubbles: true,
        detail: { handle },
      }));
    }
  }

  if (!customElements.get('pdp-size-picker')) {
    customElements.define('pdp-size-picker', PdpSizePicker);
  }
})();
